import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { DownloadJobStatus } from '../models/download-job';
import { DataService } from './data.service';
import { SignalrService } from './signalr.service';
import { SnackbarService } from './snackbar.service';

/**
 * Follows the signed-in user's background ZIP download, whichever grid started it: progress arrives over SignalR,
 * and the server is asked for the status when the page loads or the connection comes back, in case a message was missed.
 * It lives for the whole session, so progress is not lost when the user moves between tabs.
 */
/** How long to wait before looking again after a temporary failure, or while another tab is saving the file. */
const CHECK_DELAY_MS = 3000;

/** How many times fetching a ZIP may fail before it is given up on. */
const MAX_FETCH_ATTEMPTS = 3;

/** What happened to one attempt to save a ZIP. */
type SaveOutcome = 'saved' | 'retry' | 'gave-up';

@Injectable({
    providedIn: 'root'
})
export class DownloadJobService {
    private readonly apiUrl = 'api/downloads';
    private readonly jobSubject = new BehaviorSubject<DownloadJobStatus | null>(null);
    private downloadingJobId: string | null = null;
    /** Downloads this window has finished with (saved, taken by another window, or given up on): none may be fetched again. */
    private readonly savedJobIds = new Set<string>();
    private starting = false;
    /** How many times fetching each job's ZIP has failed, so a ZIP that really cannot be fetched is given up on. */
    private readonly failedFetches = new Map<string, number>();
    private checkTimer: ReturnType<typeof setTimeout> | null = null;

    /** The current job: being prepared, or ready and not yet downloaded. */
    readonly job$ = this.jobSubject.asObservable();

    constructor(
        private dataService: DataService,
        private http: HttpClient,
        private signalr: SignalrService,
        private snackbar: SnackbarService) {
        this.signalr.downloadJob$.subscribe(status => this.apply(status, true));
        this.signalr.reconnected$.subscribe(() => void this.refresh());
    }

    get job(): DownloadJobStatus | null {
        return this.jobSubject.value;
    }

    /** True while the server is still preparing a ZIP; only one can be prepared per user at a time. */
    get isBusy(): boolean {
        const status = this.job?.status;
        return status === 'Queued' || status === 'Running';
    }

    /** True while a ZIP is being started or prepared; a user may have only one at a time. */
    get inProgress(): boolean {
        return this.starting || this.isBusy;
    }

    /**
     * Asks the server to start a download and follows it. Every grid that offers downloads starts them this way, so
     * the messages and the "already have one running" handling are the same everywhere.
     */
    async begin(start: () => Promise<DownloadJobStatus>): Promise<void> {
        if (this.inProgress) return;

        this.starting = true;
        try {
            this.track(await start());
            this.snackbar.successSnackBar('Your ZIP is being prepared. It will download automatically when it is ready.');
        } catch (err) {
            if ((err as { status?: number })?.status === 409) {
                this.snackbar.errorSnackBar('You already have a download in progress. Please wait for it to finish.');
                void this.refresh();
            } else {
                this.snackbar.errorSnackBar('The download could not be started. Please try again.');
            }
        } finally {
            this.starting = false;
        }
    }

    /** Connects for notifications and picks up a job that was already running or ready. Safe to call repeatedly. */
    async initialize(): Promise<void> {
        await this.signalr.ensureUserConnection();
        await this.refresh();
    }

    /** Asks the server where the user's download is, to catch up on anything missed. */
    async refresh(): Promise<void> {
        try {
            const status = await this.dataService.get<DownloadJobStatus | null>(`${this.apiUrl}/active`);
            if (status) {
                this.apply(status, false);
            } else if (this.isBusy) {
                // Nothing active on the server but this page thinks a job is running: it finished while unseen.
                const finished = await this.dataService.get<DownloadJobStatus>(`${this.apiUrl}/${this.job!.jobId}`);
                this.apply(finished, false);
            } else if (this.job?.status === 'Completed') {
                // The ready ZIP is no longer waiting on the server: another window of this user saved it.
                this.jobSubject.next(null);
            }
        } catch (err) {
            console.error('Could not read the download status', err);
        }
    }

    /** Starts following a job this page has just started; it is saved as soon as it is ready. */
    track(status: DownloadJobStatus): void {
        this.apply(status, false);
    }

    /** Saves the finished ZIP to the user's computer; the server deletes it once it has been delivered. */
    async download(): Promise<void> {
        const job = this.job;
        if (!job || job.status !== 'Completed' || this.downloadingJobId === job.jobId || this.savedJobIds.has(job.jobId)) return;

        this.downloadingJobId = job.jobId;
        let outcome: SaveOutcome | null;
        try {
            // Every open tab of the user hears "ready", but the server hands the file out once: only one tab fetches it.
            outcome = await this.withTabLock(job.jobId, () => this.saveFile(job));
        } finally {
            this.downloadingJobId = null;
        }

        if (outcome === 'retry') {
            // A temporary failure: the ZIP is still waiting on the server, so ask again shortly.
            this.scheduleCheck(() => void this.download());
        } else if (outcome === null) {
            // Another tab is saving it. Look again shortly: when it is done this tab drops the job from view, and if that tab
            // went away mid-transfer this one takes over.
            this.scheduleCheck(() => void this.refresh().then(() => this.download()));
        }
    }

    /** Runs a check once, a few seconds from now; a check that is already waiting makes another one unnecessary. */
    private scheduleCheck(action: () => void): void {
        if (this.checkTimer !== null) return;

        this.checkTimer = setTimeout(() => {
            this.checkTimer = null;
            action();
        }, CHECK_DELAY_MS);
    }

    /** Runs the work if no other tab of this browser is already doing it for the job; null means another tab is. */
    private async withTabLock<T>(jobId: string, work: () => Promise<T>): Promise<T | null> {
        const locks = (navigator as Navigator & { locks?: LockManager }).locks;
        if (!locks) {
            return await work();
        }

        return await locks.request(`xp-zip-${jobId}`, { ifAvailable: true }, async lock => (lock ? await work() : null));
    }

    private async saveFile(job: DownloadJobStatus): Promise<SaveOutcome> {
        try {
            const blob = await this.http.get(`${this.apiUrl}/${job.jobId}/file`, { responseType: 'blob' }).toPromise();
            if (!blob) return 'retry';

            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = job.fileName || 'Requests_PDFs.zip';
            anchor.click();
            window.URL.revokeObjectURL(url);

            this.finishWith(job.jobId);
            if (job.failed > 0) {
                this.snackbar.errorSnackBar(`${job.failed} PDF(s) could not be created and are not in the ZIP. They are listed in failed.txt inside it.`);
            }
            return 'saved';
        } catch (err) {
            console.error('ZIP download failed', err);
            return await this.afterFailedFetch(job);
        }
    }

    /** Decides what a failed fetch means by asking the server where the ZIP is now. */
    private async afterFailedFetch(job: DownloadJobStatus): Promise<SaveOutcome> {
        const latest = await this.dataService.get<DownloadJobStatus>(`${this.apiUrl}/${job.jobId}`).catch(() => null);

        // Another window of this user may already have saved it (the server hands the file out once): that is not an error.
        if (latest?.status === 'Downloaded') {
            this.finishWith(job.jobId);
            return 'saved';
        }

        // Still waiting on the server (or the server could not be asked): the failure was probably temporary.
        const attempts = (this.failedFetches.get(job.jobId) ?? 0) + 1;
        this.failedFetches.set(job.jobId, attempts);
        if ((latest === null || latest.status === 'Completed') && attempts < MAX_FETCH_ATTEMPTS) {
            return 'retry';
        }

        this.snackbar.errorSnackBar('The ZIP could not be downloaded. It may have expired; please start the download again.');
        this.finishWith(job.jobId);
        return 'gave-up';
    }

    /** This window is done with the job: it is saved, taken by another window, or given up on. */
    private finishWith(jobId: string): void {
        this.savedJobIds.add(jobId);
        this.failedFetches.delete(jobId);
        this.clearIfCurrent(jobId);
    }

    /** Removes a failed job from view. */
    dismiss(): void {
        this.jobSubject.next(null);
    }

    /** Drops the job from view, unless a newer download has taken its place meanwhile. */
    private clearIfCurrent(jobId: string): void {
        if (this.job?.jobId === jobId) this.jobSubject.next(null);
    }

    private apply(status: DownloadJobStatus, live: boolean): void {
        // Whatever the server still says about a ZIP this window has already saved is stale.
        if (this.savedJobIds.has(status.jobId)) return;

        const current = this.job;
        const isActive = (state: string) => state === 'Queued' || state === 'Running';

        // A late progress message must never take a finished job back to "running".
        if (current && current.jobId === status.jobId && !isActive(current.status) && isActive(status.status)) return;

        if (status.status === 'Downloaded' || status.status === 'Expired') {
            if (current?.jobId === status.jobId) this.jobSubject.next(null);
            return;
        }

        const changed = !current || current.jobId !== status.jobId || current.status !== status.status;
        this.jobSubject.next(status);

        if (status.status === 'Completed') {
            // Saved as soon as it is ready, whether it was started on this page or found after a reload. A ZIP that is
            // still waiting is tried again whenever the server is asked about it, not only when its status changes, so a
            // fetch that failed earlier is not left behind; download() ignores the call while one is already running.
            void this.download();
        } else if (changed && status.status === 'Failed' && live) {
            this.snackbar.errorSnackBar(status.message || 'The download could not be prepared. Please try again.');
        }
    }
}
